import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "fc_session_token";

export const getStoredToken = () => {
  try { return window.localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
};

export const setStoredToken = (token) => {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch { /* storage disabled — cookie path still works on same-origin */ }
};

export const http = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  // Auth is carried via the Authorization: Bearer header below. Cookies
  // are intentionally NOT relied on cross-origin: some hosting layers add
  // `Access-Control-Allow-Origin: *` together with credentials, which
  // browsers reject for `credentials: include` requests, even when the
  // backend returned a valid 200. Same-origin cookies still flow either
  // way; the Bearer token covers the cross-origin/proxy case.
  withCredentials: false,
});

http.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

export const listProjects = () => http.get("/projects").then(r => r.data);
export const createProject = (payload) => http.post("/projects", payload).then(r => r.data);
export const getProject = (id) => http.get(`/projects/${id}`).then(r => r.data);
export const updateProject = (id, payload) => http.put(`/projects/${id}`, payload).then(r => r.data);
export const deleteProject = (id) => http.delete(`/projects/${id}`).then(r => r.data);
export const duplicateProject = (id) => http.post(`/projects/${id}/duplicate`).then(r => r.data);export const listComparables = (id, tolerance = 0.30) =>
  http.get(`/projects/${id}/comparables`, { params: { tolerance } }).then(r => r.data);

export const getCostLibrary = (projectId) => http.get(`/projects/${projectId}/cost-library`).then(r => r.data);
export const updateCostLibrary = (projectId, payload) => http.put(`/projects/${projectId}/cost-library`, payload).then(r => r.data);

// ---------- Project Sharing ----------
export const listShares = (projectId) =>
  http.get(`/projects/${projectId}/shares`).then(r => r.data);
export const createShare = (projectId, payload) =>
  http.post(`/projects/${projectId}/shares`, payload).then(r => r.data);
export const updateShare = (projectId, shareId, payload) =>
  http.patch(`/projects/${projectId}/shares/${shareId}`, payload).then(r => r.data);
export const revokeShare = (projectId, shareId) =>
  http.delete(`/projects/${projectId}/shares/${shareId}`).then(r => r.data);
export const resendShare = (projectId, shareId) =>
  http.post(`/projects/${projectId}/shares/${shareId}/resend`).then(r => r.data);
export const lookupInvite = (token) =>
  http.get(`/invite/${token}`).then(r => r.data);
export const acceptInvite = (token) =>
  http.post(`/invite/${token}/accept`).then(r => r.data);

// ---------- Public Contact ----------
export const submitContactMessage = (payload) =>
  http.post(`/contact`, payload).then(r => r.data);

// ---------- Pricing Waitlist ----------
export const submitWaitlistSignup = (payload) =>
  http.post(`/waitlist`, payload).then(r => r.data);
export const getWaitlistCount = () =>
  http.get(`/waitlist/count`).then(r => r.data);

// ---------- Analytics ----------
// Fire-and-forget event ping. Never throws — we'd rather lose a sample
// than break the user's interaction.
export const trackEvent = (event, source, meta) => {
  try {
    return http
      .post(`/analytics/event`, { event, source, meta })
      .catch(() => null);
  } catch {
    return null;
  }
};


// ---------- Password Reset ----------
export const requestPasswordReset = (email) =>
  http.post(`/auth/forgot-password`, { email }).then(r => r.data);
export const lookupResetToken = (token) =>
  http.get(`/auth/reset-password/${token}`).then(r => r.data);
export const resetPassword = (token, new_password) =>
  http.post(`/auth/reset-password`, { token, new_password }).then(r => r.data);

// ---------- Billing ----------
export const billingStatus = () => http.get(`/billing/status`).then(r => r.data);
export const billingCheckout = (payload) => http.post(`/billing/checkout`, payload).then(r => r.data);
export const billingVerify = (reference) => http.get(`/billing/verify/${reference}`).then(r => r.data);
export const billingCancel = () => http.post(`/billing/cancel`).then(r => r.data);
